'use client';

import {
  useGetAllExpressZones,
  useGetAllLocationList,
  useGetStoreList,
  useGetThanaAddressList,
} from '@/hooks/location';
import { ILocation, IStore } from '@/interfaces/location';
import { getLocationData } from '@/helpers/address-helper';
import { IOption } from '@/interfaces/store';
import useHeaderStore from '@/store/header';
import { useEffect, useMemo, useRef } from 'react';

/** Sentinel returned by /api/express-zone when no zone matches the thana.
 *  Lives in one place so test fixtures and the canonical writer stay in sync. */
export const EXPRESS_ZONE_SENTINEL = 'Express not found';

// Module-scoped so the predicate has a stable identity across renders.
const isRealStore = (s: IStore): boolean =>
  Boolean(s?.LocationRefcode) && s.LocationRefcode !== EXPRESS_ZONE_SENTINEL;

export interface UseLocationModalQueriesArgs {
  showLocationModal: boolean;
  isBopis: boolean;
  divisionId: string;
  districtId: string;
  thanaId: string;
  myLocationValue: string;
  userCurrentLatLong?: { latitude?: number; longitude?: number } | null;
  /** When set AND isBopis=true, queries /api/store/list/store-changed. */
  accessToken?: string;
}

export interface UseLocationModalQueries {
  districts: IOption[];
  thanas: IOption[];
  allLocationList?: any;
  thanaData?: ILocation[];
  storeList?: any;
  expressZones?: any;
  isStoreListLoading: boolean;
  /** True while a storeList refetch is in-flight. `isLoading` only flips on
   *  the first mount; the store query uses `placeholderData: previousData`
   *  so `isFetching` is the right gate when waiting for a new filter. */
  isStoreListFetching: boolean;
}

export const useLocationModalQueries = ({
  showLocationModal,
  isBopis,
  divisionId,
  districtId,
  thanaId,
  myLocationValue,
  userCurrentLatLong,
  accessToken,
}: UseLocationModalQueriesArgs): UseLocationModalQueries => {
  const selectedStore = useHeaderStore((state) => state.selectedStore);
  const selectedStoreIds = useHeaderStore((state) => state.selectedStoreIds);
  const selectedStoreCodes = useHeaderStore((state) => state.selectedStoreCodes);
  const setSelectedStoreIds = useHeaderStore((state) => state.setSelectedStoreIds);
  const setSelectedStoreCodes = useHeaderStore((state) => state.setSelectedStoreCodes);
  const setExpressMessage = useHeaderStore((state) => state.setExpressMessage);
  const setIsExpressEnabled = useHeaderStore((state) => state.setIsExpressEnabled);
  // Read `isExpressEnabled` up-front so the Express-zones fetch below can fall
  // back to `myLocationValue` on PLP loads where the modal is closed but
  // Express is already enabled (see D-05). Without this lift, the call to
  // `useGetAllExpressZones` would always gate on `thanaId !== '-1'` and miss
  // the session-restored location.
  const isExpressEnabled = useHeaderStore((state) => state.isExpressEnabled);

  const { data: allLocationList } = useGetAllLocationList({
    enabled: showLocationModal,
    params: {
      IncludeDivisions: true,
      IncludeDistricts: true,
      IncludeThanas: true,
    },
  });

  const { data: thanaData } = useGetThanaAddressList(districtId !== '-1' ? districtId : undefined);

  // Endpoint selection is handled inside `getStoreLists`:
  //   - isBopis=true && accessToken → /api/store/list/store-changed (auth'd)
  //   - else → /api/store/list (public)
  // Non-Checkout callers (FilterPanel, PreHeaderStoreSelector, Header) open the
  // BOPIS modal without an accessToken and rely on the public endpoint returning
  // the regular store list. We must not disable the query for them.
  const isStoreListEnabled = showLocationModal;

  const { data: storeList, isLoading: isStoreListLoading, isFetching: isStoreListFetching } = useGetStoreList(
    divisionId !== '-1' ? divisionId : undefined,
    districtId !== '-1' ? districtId : undefined,
    thanaId !== '-1' ? thanaId : undefined,
    undefined,
    userCurrentLatLong?.latitude,
    userCurrentLatLong?.longitude,
    isStoreListEnabled,
    isBopis ? true : null,
    true,
    accessToken,
  );

  // D-05: Lift modal-only gating off the canonical writer. When the modal is
  // closed (so `thanaId` stays `'-1'`) but Express is already enabled from a
  // previous session with `myLocationValue` set in the header store, the
  // canonical writer must still be able to fetch zones. `myLocationValue` IS a
  // thanaId — see LocationModal.tsx `setMyLocation({value: thanaId, ...})` —
  // so we use it as the fetch key in that case. Enabling Express from
  // FilterPanel must NOT open the modal when a location is already set; this
  // hook now reacts to the store-driven state directly.
  const effectiveThanaId =
    thanaId !== '-1'
      ? thanaId
      : isExpressEnabled && myLocationValue
        ? myLocationValue
        : '-1';
  const { data: expressZones } = useGetAllExpressZones(effectiveThanaId);

  // De-dup ref so sentinel responses don't loop on `setSelectedStoreIds([])`.
  const lastHandledZonesRef = useRef<unknown>(null);

  useEffect(() => {
    if (!expressZones) {
      return;
    }

    // Early-return when there is nothing to write. Do NOT record
    // `expressZones` into the ref here — the next enable cycle must
    // re-process this exact zones object to propagate sentinel codes.
    if (!isExpressEnabled && !myLocationValue) {
      return;
    }

    // No D-08 short-circuit: legitimate Express zones with `Stores: []`
    // (e.g. `{ZoneName: "Express Zone", Stores: []}`) must still fall through
    // so the writer clears `selectedStoreIds` and writes the empty codes.
    // The ref de-dup below prevents the storm — same effect, broader coverage.

    // Re-run if codes are stale (post disable→enable cycle) so the sentinel
    // propagates; otherwise de-dup on the zones ref.
    const codesAreStale = (selectedStoreCodes?.length ?? 0) === 0 && isExpressEnabled;
    if (!codesAreStale && lastHandledZonesRef.current === expressZones) {
      return;
    }

    const firstCode = expressZones.Stores?.[0]?.LocationRefcode;

    if (firstCode === EXPRESS_ZONE_SENTINEL) {
      setExpressMessage(
        'Express delivery is not available for this location. Please select another location or choose home delivery.',
      );
      lastHandledZonesRef.current = expressZones;
      // Propagate the sentinel into selectedStoreCodes so /api/search
      // returns an empty product list for this thana — keeps the
      // Express-not-available state visible in the product list itself
      // instead of falling back to the cross-store inventory.
      setSelectedStoreIds([]);
      setSelectedStoreCodes(expressZones.Stores.map((s: IStore) => s.LocationRefcode));
      return;
    }

    if (!selectedStore?.Id) {
      const realStores = (expressZones.Stores ?? []).filter(isRealStore);
      if (realStores.length === 0) {
        setExpressMessage(
          'Express delivery is not available for this location. Please select another location or choose home delivery.',
        );
        setSelectedStoreIds([]);
        setSelectedStoreCodes([EXPRESS_ZONE_SENTINEL]);
        lastHandledZonesRef.current = expressZones;
      } else {
        const storeCodes: string[] = realStores.map((s: IStore) => s.LocationRefcode);
        const storeIds: string[] = realStores.map((s: IStore) => s.Id);
        lastHandledZonesRef.current = expressZones;
        setSelectedStoreIds(storeIds);
        setSelectedStoreCodes(storeCodes);
        setExpressMessage('');
      }
    }
  }, [
    expressZones,
    selectedStoreIds,
    selectedStoreCodes,
    selectedStore?.Id,
    isExpressEnabled,
    myLocationValue,
    setSelectedStoreIds,
    setSelectedStoreCodes,
    setExpressMessage,
    // setIsExpressEnabled removed — never called inside this effect.
  ]);

  // Districts always derived from bulk allLocationList.Districts.
  const districts = useMemo<IOption[]>(() => {
    const list: IOption[] = [{ label: 'All Districts', value: '-1' }];
    const source = allLocationList?.Districts as ILocation[] | undefined;
    const data = getLocationData(source) ?? [];
    [...data]
      .sort((a, b) => a.label.localeCompare(b.label, 'en'))
      .forEach((d) => list.push(d));
    return list;
  }, [allLocationList]);

  const thanas = useMemo<IOption[]>(() => {
    const sentinel: IOption = { label: 'All Thanas', value: '-1' };
    let next: IOption[] = [sentinel];
    const allThanas = allLocationList?.Thanas as any[] | undefined;

    // District selected → prefer per-district detail fetch.
    if (districtId !== '-1' && (thanaData as ILocation[] | undefined)?.length) {
      (thanaData as ILocation[]).forEach((t) => {
        if (t?.Id) {
          next.push({ label: t.Name ?? '', value: t.Id });
        }
      });
      const items = [...next.slice(1)].sort((a, b) => a.label.localeCompare(b.label, 'en'));
      return [sentinel, ...items];
    }

    // No district selected → fall back to full bulk thana list.
    (allThanas ?? []).forEach((t) => {
      if (t?.Id) {
        next.push({ label: t.Name ?? '', value: t.Id });
      }
    });
    const items = [...next.slice(1)].sort((a, b) => a.label.localeCompare(b.label, 'en'));
    return [sentinel, ...items];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLocationList, thanaData, districtId]);

  return {
    districts,
    thanas,
    allLocationList,
    thanaData,
    storeList,
    expressZones,
    isStoreListLoading,
    isStoreListFetching,
  };
};

export default useLocationModalQueries;
